















































































import { appointmentProductTypeDescription, appointmentTypeDescription, instructionTypeDescription, locationTypeDescription, outcomePaymentStatusTypeDescription, outcomeTypeDescription, productPaymentStatusTypeDescription } from '@/components/appointments/appointment-utilities';
import { Appointment } from '@/shared/appointments/appointment';
import { AppointmentClient } from '@/shared/appointments/appointment-client';
import { AppointmentOutcomeProduct } from '@/shared/appointments/appointment-outcome-product';
import { AppointmentProductPaymentDetails } from '@/shared/appointments/appointment-product-payment-details';
import { InstructionType } from '@/shared/instruction-type';
import { LocationType } from '@/shared/location-type';
import { AppointmentOutcomeType } from '@/shared/types/appointment-outcome-type';
import { AppointmentType } from '@/shared/types/appointment-type';
import { User } from '@/shared/user';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const partnerPracticesModule = namespace('partnerPractices');
const typesModule = namespace('types');
const usersModule = namespace('users');

@Component
export default class AppointmentsTable extends Vue {
    @partnerPracticesModule.Getter('partnerPracticeName') private partnerPracticeName!: (partnerPracticeId: string | null) => string;

    @typesModule.Getter('appointmentTypes') public appointmentTypes!: Array<AppointmentType>;
    @typesModule.Getter('appointmentOutcomeTypes') public outcomeTypes!: Array<AppointmentOutcomeType>;

    @usersModule.Getter('users') public users!: Array<User>;

    @Prop({ required: true }) public appointments!: Array<Appointment>;

    private currentPage: number = 1;
    private perPage: number = 25;
    private sortBy: string = 'date';
    private sortDesc: boolean = true;

    public get appointmentFields(): Array<{}> {
        const fields = [
            { key: 'status', label: 'Status', sortable: false },
            { key: 'partnerPractice', label: 'Assigned To', sortable: true },
            { key: 'accountNumber', label: 'Account Number', sortable: true },
            { key: 'clientName', label: 'Client Name', sortable: true },
            { key: 'legalAdvisor', label: 'Legal Advisor', sortable: true },
            { key: 'appointmentType', label: 'Type', sortable: true },
            { key: 'location', label: 'Location', sortable: true },
            { key: 'date', label: 'Date', sortable: true },
            { key: 'time', label: 'Time', sortable: false },
            { key: 'appointmentBooker', label: 'Booker', sortable: true },
            { key: 'actions', label: '', class: 'col-1', tdClass: 'text-right' },
        ];

        return fields;
    }

    private get totalRows(): number {
        return this.appointments.length;
    }

    private get paginationDescription(): string {
        const start = (this.appointments.length < 1)
            ? 0
            : ((this.currentPage - 1) * this.perPage) + 1;

        const end = start + this.perPage > this.totalRows
            ? this.totalRows
            : start + this.perPage - 1;

        return `Showing ${start.toLocaleString()} - ${end.toLocaleString()} of ${this.totalRows.toLocaleString()}`;
    }

    @Watch('appointments', { deep: true })
    private onAppointmentsChanged(): void {
        this.currentPage = 1;
    }

    private onSortTable(rowA: Appointment, rowB: Appointment, key: string): number | null {
        switch (key) {
            case 'partnerPractice':
                const partnerPracticeA = this.partnerPracticeName(rowA.partnerPracticeId);
                const partnerPracticeB = this.partnerPracticeName(rowB.partnerPracticeId);

                return (partnerPracticeA > partnerPracticeB) ? 1 : (partnerPracticeA < partnerPracticeB) ? -1 : 0;

            case 'accountNumber':
                const accountNoA = rowA.clients[0].accountNo!;
                const accountNoB = rowB.clients[0].accountNo!;

                return (accountNoA > accountNoB) ? 1 : (accountNoA < accountNoB) ? -1 : 0;

            case 'clientName':
                const clientNameA = rowA.clients[0].lastName!.trim().toUpperCase();
                const clientNameB = rowB.clients[0].lastName!.trim().toUpperCase();

                return (clientNameA > clientNameB) ? 1 : (clientNameA < clientNameB) ? -1 : 0;

            case 'legalAdvisor':
                const legalAdvisorA = this.legalAdvisorName(rowA);
                const legalAdvisorB = this.legalAdvisorName(rowB);

                return (legalAdvisorA > legalAdvisorB) ? 1 : (legalAdvisorA < legalAdvisorB) ? -1 : 0;

            case 'appointmentType':
                const appointmentTypeA = this.appointmentTypeDescription(rowA.appointmentTypeId);
                const appointmentTypeB = this.appointmentTypeDescription(rowB.appointmentTypeId);

                return (appointmentTypeA > appointmentTypeB) ? 1 : (appointmentTypeA < appointmentTypeB) ? -1 : 0;

            case 'location':
                const locationTypeA = this.locationTypeDescription(rowA.locationType!);
                const locationTypeB = this.locationTypeDescription(rowB.locationType!);

                return (locationTypeA > locationTypeB) ? 1 : (locationTypeA < locationTypeB) ? -1 : 0;

            case 'date':
                return (rowA.date! > rowB.date!) ? 1 : (rowA.date! < rowB.date!) ? -1 : 0;

            case 'appointmentBooker':
                const bookerNameA = rowA.booker!.name!;
                const bookerNameB = rowB.booker!.name!;

                return (bookerNameA > bookerNameB) ? 1 : (bookerNameA < bookerNameB) ? -1 : 0;

            default:
                return null;
        }
    }

    private isUnsaved(appointment: Appointment): boolean {
        return (appointment.id == null) || (appointment.outcome != null && appointment.outcome.id == null);
    }

    private isRebook(appointment: Appointment): boolean {
        return appointment.originalAppointmentId != null;
    }

    private isRebooked(appointment: Appointment): boolean {
        return appointment.isRebooked!;
    }

    private isPending(appointment: Appointment): boolean {
        return appointment.outcome == null && !appointment.isRebooked;
    }

    private showOutcomeType(appointment: Appointment): boolean {
        return appointment.outcome != null;
    }

    private showInstructionType(appointment: Appointment): boolean {
        if (appointment.outcome == null) {
            return false;
        }

        const outcome = appointment.outcome;

        const appointmentType = this.appointmentTypes.find((type) => type.id === appointment.appointmentTypeId);

        if (appointmentType == null || !appointmentType.isInitialAppointment) {
            return false;
        }

        const outcomeType = this.outcomeTypes.find((type) => type.id === outcome.outcomeTypeId);

        return outcomeType != null && outcomeType.isSat;
    }

    private showNotInstructed(appointment: Appointment): boolean {
        if (appointment.outcome == null || appointment.outcome.isLegacy) {
            return false;
        }

        const appointmentType = this.appointmentTypes.find((type) => type.id === appointment.appointmentTypeId);

        if (appointmentType == null || !appointmentType.isInitialAppointment) {
            return false;
        }

        const outcomeType = this.outcomeTypes.find((type) => type.id === appointment.outcome!.outcomeTypeId);

        if (outcomeType == null || !outcomeType.isSat) {
            return false;
        }

        return appointment.outcome.products == null || !appointment.outcome.products.length;
    }

    private showOutcomePaymentStatus(appointment: Appointment): boolean {
        if (appointment.outcome == null) {
            return false;
        }

        return appointment.outcome.isLegacy && appointment.outcome.instructionType === InstructionType.Instructed;
    }

    private showOutcomeProducts(appointment: Appointment): boolean {
        if (appointment.outcome == null) {
            return false;
        }

        return !appointment.outcome.isLegacy && appointment.outcome.products.length > 0;
    }

    private showProductPaymentStatus(product: AppointmentOutcomeProduct): boolean {
        if (product == null) {
            return false;
        }

        return product.instructionType === InstructionType.Instructed;
    }

    private outcomeTypeDescription(outcomeTypeId: string | null): string {
        return outcomeTypeDescription(outcomeTypeId);
    }

    private instructionTypeDescription(instructionType: InstructionType): string {
        return instructionTypeDescription(instructionType);
    }

    private appointmentProductTypeDescription(productTypeId: string): string {
        return appointmentProductTypeDescription(productTypeId);
    }

    private outcomePaymentStatusTypeDescription(paymentStatusId: string | null) {
        return outcomePaymentStatusTypeDescription(paymentStatusId);
    }

    private productPaymentStatusTypeDescription(paymentDetails: Array<AppointmentProductPaymentDetails> | null): string {
        return productPaymentStatusTypeDescription(paymentDetails);
    }

    private appointmentTypeDescription(appointmentTypeId: string | null): string {
        return appointmentTypeDescription(appointmentTypeId);
    }

    private locationTypeDescription(locationType: LocationType): string {
        return locationTypeDescription(locationType);
    }

    private accountNumber(appointment: Appointment): string {
        const accountNumbers = appointment.clients.map((client: AppointmentClient) => {
            return client.accountNo;
        });

        return accountNumbers.join(' & ');
    }

    private clientName(appointment: Appointment): string {
        if (appointment.clients == null || appointment.clients.length < 1) {
            return '';
        }

        if (appointment.clients.length < 2) {
            return `${appointment.clients[0].firstName} ${appointment.clients[0].lastName}`;
        }

        if (appointment.clients[0].lastName != null && appointment.clients[1].lastName != null && appointment.clients[0].lastName.toLowerCase() === appointment.clients[1].lastName.toLowerCase()) {
            return `${appointment.clients[0].firstName} & ${appointment.clients[1].firstName} ${appointment.clients[0].lastName}`;
        }

        return `${appointment.clients[0].firstName} ${appointment.clients[0].lastName} & ${appointment.clients[1].firstName} ${appointment.clients[1].lastName}`;
    }

    private legalAdvisorName(appointment: Appointment): string {
        const advisor = this.users.find((user) => user.id === appointment.legalAdvisorId);

        if (advisor == null) {
            return '';
        }

        return `${advisor.firstName == null ? '' : advisor.firstName} ${advisor.lastName == null ? '' : advisor.lastName}`;
    }
}

